import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import {
  SignalColorField,
  SignalDropdown,
  SignalTextField,
} from '@vallift/ngl';

import type {
  DividerOpacity,
  DividerStyle,
  DividerThickness,
  FormField,
} from '../../../../models/built-form';

interface SelectOption<T> {
  label: string;
  value: T;
}

interface DividerConfigModel {
  label: string;
  dividerStyle: DividerStyle;
  dividerThickness: DividerThickness;
  dividerColor: string;
  dividerOpacity: DividerOpacity;
}

@Component({
  selector: 'app-divider-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalDropdown,
    SignalTextField,
    SignalColorField,
  ],
  templateUrl: './divider-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DividerConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly dividerStyleOptions: SelectOption<DividerStyle>[] = [
    { label: 'Solid', value: 'solid' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Dotted', value: 'dotted' },
  ];

  readonly dividerThicknessOptions: SelectOption<DividerThickness>[] = [
    { label: '1 px', value: 1 },
    { label: '2 px', value: 2 },
    { label: '3 px', value: 3 },
    { label: '4 px', value: 4 },
  ];

  readonly dividerOpacityOptions: SelectOption<DividerOpacity>[] = [
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 },
  ];

  readonly model = signal<DividerConfigModel>({
    label: '',
    dividerStyle: 'solid',
    dividerThickness: 1,
    dividerColor: '#e2e8f0',
    dividerOpacity: 100,
  });

  readonly configForm = form(this.model);
  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      this.model.set({
        label: next.label,
        dividerStyle: next.dividerStyle ?? 'solid',
        dividerThickness: next.dividerThickness ?? 1,
        dividerColor: next.dividerColor ?? '#e2e8f0',
        dividerOpacity: next.dividerOpacity ?? 100,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim() || field.label,
        dividerStyle: draft.dividerStyle,
        dividerThickness: draft.dividerThickness,
        dividerColor: draft.dividerColor,
        dividerOpacity: draft.dividerOpacity,
        placeholder: undefined,
        helpText: undefined,
        required: undefined,
        validation: undefined,
        options: undefined,
        spacerSize: undefined,
      });
    });
  }
}
